import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ChecklistAdminEntity,
  ChecklistAdminField,
  ChecklistAdminItem,
  ChecklistEditPermission,
  createChecklistAdminItem,
  createChecklistField,
  getChecklistShareTemplate,
  listChecklistAdminItems,
  listChecklistEditPermissions,
  listChecklistFields,
  reorderChecklistAdminItems,
  reorderChecklistFields,
  updateChecklistAdminItem,
  updateChecklistEditPermission,
  updateChecklistField,
  updateChecklistShareTemplate,
} from '../../../api/checklistAdminApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import HistoricoAlteracoes from '../../../components/historico-alteracoes/HistoricoAlteracoes';
import '../../../components/como-funciona/help-circle-btn.css';
// Esqueleto de cabeçalho compartilhado (`recepcao-header-row`, `recepcao-title`). O import é
// explícito de propósito: contar com o CSS que a página hospedeira carrega é acoplamento
// invisível — a aba quebraria em silêncio se fosse montada em outra tela (lição do BUG-V74).
import '../../recepcao/pages/Recepcao.css';
import { removerAcentos } from '../../../shared/texto/busca-sem-acento';

const ALL_ROLES = [
  'RECEPCAO', 'CHEFE_DE_OFICINA', 'COLABORADOR', 'ORCAMENTO',
  'COMPRA', 'ESTOQUE',
];

const PERMISSION_LABELS: Record<string, { title: string; description: string }> = {
  EDITAR_ITENS_SESSAO: {
    title: 'Editar itens de checklists concluídos',
    description: 'Corrigir status e observações dos itens de sessões já finalizadas (toda edição é auditada).',
  },
  EDITAR_MIDIAS_SESSAO: {
    title: 'Adicionar/remover mídias de checklists',
    description: 'Enviar e excluir fotos/vídeos de sessões. Inclui o fluxo normal de preenchimento (as mídias são enviadas após a conclusão da sessão).',
  },
};

type SubTab = 'angles' | 'categories' | 'items' | 'permissions' | 'share-template';

const FIELD_TABS: Array<{ key: SubTab; entity?: ChecklistAdminEntity; label: string; hasHelp?: boolean }> = [
  { key: 'angles', entity: 'angles', label: 'Ângulos de Fotos', hasHelp: true },
  { key: 'categories', entity: 'categories', label: 'Categorias de Itens' },
  { key: 'items', label: 'Itens' },
  { key: 'permissions', label: 'Permissões de Edição' },
  { key: 'share-template', label: 'Mensagem de Compartilhamento' },
];

const labelToKey = (label: string): string =>
  removerAcentos(label)
    .toUpperCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^A-Z0-9_]/g, '');

const ChecklistAdminSection: React.FC = () => {
  const [subTab, setSubTab] = useState<SubTab>('angles');
  const [loading, setLoading] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isHistoricoOpen, setIsHistoricoOpen] = useState(false);

  const [fields, setFields] = useState<ChecklistAdminField[]>([]);
  const [items, setItems] = useState<ChecklistAdminItem[]>([]);
  const [categories, setCategories] = useState<ChecklistAdminField[]>([]);
  const [permissions, setPermissions] = useState<ChecklistEditPermission[]>([]);

  // Form de criação
  const [newLabel, setNewLabel] = useState('');
  const [newHelpText, setNewHelpText] = useState('');
  const [newCategoryId, setNewCategoryId] = useState('');
  const [creating, setCreating] = useState(false);

  // Edição inline de label
  const [editKey, setEditKey] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');

  // Template da mensagem de compartilhamento via WhatsApp
  const [shareTemplate, setShareTemplate] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Reordenação em andamento (at-50) — trava as setas até a resposta do backend
  const [reordering, setReordering] = useState(false);

  const currentTab = FIELD_TABS.find((t) => t.key === subTab)!;
  const isFieldTab = Boolean(currentTab.entity);

  // A reordenação só vale entre ativos (inativos não entram em checklist nenhum)
  const activeFields = useMemo(() => fields.filter((field) => field.isActive), [fields]);

  const refresh = async () => {
    setLoading(true);
    try {
      if (currentTab.entity) {
        setFields(await listChecklistFields(currentTab.entity));
      } else if (subTab === 'items') {
        const [itemList, categoryList] = await Promise.all([
          listChecklistAdminItems(),
          listChecklistFields('categories'),
        ]);
        setItems(itemList);
        setCategories(categoryList);
        if (!newCategoryId) {
          const firstActive = categoryList.find((c) => c.isActive);
          if (firstActive) setNewCategoryId(firstActive.id);
        }
      } else if (subTab === 'permissions') {
        setPermissions(await listChecklistEditPermissions());
      } else if (subTab === 'share-template') {
        const { value } = await getChecklistShareTemplate();
        setShareTemplate(value);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao carregar dados do checklist.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setEditKey(null);
    setNewLabel('');
    setNewHelpText('');
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);

  const handleCreate = async () => {
    const label = newLabel.trim();
    if (!label) return toast.error('Informe o nome do campo.');

    setCreating(true);
    try {
      if (subTab === 'items') {
        if (!newCategoryId) return toast.error('Selecione a categoria.');
        await createChecklistAdminItem({
          key: labelToKey(label).toLowerCase(),
          label,
          categoryId: newCategoryId,
        });
      } else if (currentTab.entity) {
        await createChecklistField(currentTab.entity, {
          key: labelToKey(label),
          label,
          helpText: currentTab.hasHelp ? newHelpText.trim() || undefined : undefined,
        });
      }
      setNewLabel('');
      setNewHelpText('');
      toast.success('Campo criado com sucesso.');
      void refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao criar campo.');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleField = async (field: ChecklistAdminField) => {
    try {
      if (currentTab.entity) {
        await updateChecklistField(currentTab.entity, field.id, { isActive: !field.isActive });
      }
      toast.success(`Campo "${field.label}" ${field.isActive ? 'desativado' : 'reativado'}.`);
      void refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao alterar status do campo.');
    }
  };

  const handleSaveLabel = async (idOrKey: string) => {
    const label = editLabel.trim();
    if (!label) return toast.error('Informe o novo nome.');
    try {
      if (subTab === 'items') {
        await updateChecklistAdminItem(idOrKey, { label });
      } else if (currentTab.entity) {
        await updateChecklistField(currentTab.entity, idOrKey, { label });
      }
      setEditKey(null);
      toast.success('Campo atualizado.');
      void refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao atualizar campo.');
    }
  };

  const handleToggleItem = async (item: ChecklistAdminItem) => {
    try {
      await updateChecklistAdminItem(item.key, { isActive: !item.isActive });
      toast.success(`Item "${item.label}" ${item.isActive ? 'desativado' : 'reativado'}.`);
      void refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao alterar status do item.');
    }
  };

  const handleChangeItemCategory = async (item: ChecklistAdminItem, categoryId: string) => {
    try {
      await updateChecklistAdminItem(item.key, { categoryId });
      toast.success('Categoria do item atualizada.');
      void refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao mover item de categoria.');
    }
  };

  /**
   * Reordenação (at-50): as setas operam apenas entre os registros ATIVOS —
   * inativos não aparecem em checklist e ficam sempre no fim da lista enviada.
   * O backend exige a lista completa e renumera a ordem de 1 a N.
   */
  const moveInList = <T,>(list: T[], index: number, direction: -1 | 1): T[] | null => {
    const target = index + direction;
    if (target < 0 || target >= list.length) return null;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  };

  const handleMoveField = async (index: number, direction: -1 | 1) => {
    if (!currentTab.entity || reordering) return;

    const actives = fields.filter((f) => f.isActive);
    const reordered = moveInList(actives, index, direction);
    if (!reordered) return;

    const ids = [...reordered, ...fields.filter((f) => !f.isActive)].map((f) => f.id);

    setReordering(true);
    try {
      await reorderChecklistFields(currentTab.entity, ids);
      void refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao reordenar.');
    } finally {
      setReordering(false);
    }
  };

  const handleMoveItem = async (categoryId: string, index: number, direction: -1 | 1) => {
    if (reordering) return;

    const ofCategory = items.filter((item) => item.categoryId === categoryId);
    const actives = ofCategory.filter((item) => item.isActive);
    const reordered = moveInList(actives, index, direction);
    if (!reordered) return;

    const keys = [...reordered, ...ofCategory.filter((item) => !item.isActive)].map((item) => item.key);

    setReordering(true);
    try {
      await reorderChecklistAdminItems(categoryId, keys);
      void refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao reordenar os itens.');
    } finally {
      setReordering(false);
    }
  };

  /** Par de setas subir/desce — só para registros ativos. */
  const renderReorderButtons = (index: number, total: number, onMove: (direction: -1 | 1) => void) => (
    <div className="admin-reorder-group">
      <button
        type="button"
        className="admin-reorder-btn"
        title="Mover para cima"
        aria-label="Mover para cima"
        disabled={reordering || index === 0}
        onClick={() => onMove(-1)}
      >
        ▲
      </button>
      <button
        type="button"
        className="admin-reorder-btn"
        title="Mover para baixo"
        aria-label="Mover para baixo"
        disabled={reordering || index === total - 1}
        onClick={() => onMove(1)}
      >
        ▼
      </button>
    </div>
  );

  const handleTogglePermissionRole = async (permission: ChecklistEditPermission, role: string) => {
    const has = permission.allowedRoles.includes(role);
    const next = has
      ? permission.allowedRoles.filter((r) => r !== role)
      : [...permission.allowedRoles, role];
    try {
      await updateChecklistEditPermission(permission.action, next);
      toast.success('Permissão atualizada.');
      void refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao atualizar permissão.');
    }
  };

  const renderInlineLabel = (idOrKey: string, label: string, inactive: boolean) => {
    if (editKey === idOrKey) {
      return (
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <input
            className="admin-input"
            style={{ height: 32, padding: '4px 8px', fontSize: 13, width: 260 }}
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveLabel(idOrKey)}
            autoFocus
          />
          <button className="admin-btn admin-btn-primary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleSaveLabel(idOrKey)}>
            Salvar
          </button>
          <button className="admin-btn admin-btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setEditKey(null)}>
            Cancelar
          </button>
        </span>
      );
    }
    return (
      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--tema-texto, #1A1C1B)' }}>{label}</span>
        {inactive && <span className="admin-badge-inactive">INATIVO</span>}
        <button
          className="admin-btn admin-btn-secondary"
          style={{ padding: '2px 10px', fontSize: 11 }}
          onClick={() => { setEditKey(idOrKey); setEditLabel(label); }}
        >
          Renomear
        </button>
      </span>
    );
  };

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que se configura aqui',
      conteudo: (
        <p>
          A estrutura dos checklists de toda a oficina: as posições de foto (<strong>Ângulos de Fotos</strong>), as{' '}
          <strong>Categorias de Itens</strong>, os <strong>Itens</strong> de vistoria, quem pode editar checklists
          concluídos (<strong>Permissões de Edição</strong>) e a <strong>Mensagem de Compartilhamento</strong>{' '}
          enviada ao cliente por WhatsApp.
        </p>
      ),
    },
    {
      titulo: 'Ordem dos campos',
      conteudo: (
        <p>
          As setas de reordenação valem para o preenchimento, a visualização e o PDF —{' '}
          <strong>inclusive dos checklists já realizados</strong>: a exibição sempre segue a ordem atual, não uma
          foto do passado. Campos e itens desativados vão para o fim da lista e perdem as setas.
        </p>
      ),
    },
    {
      titulo: 'Desativar preserva o histórico',
      conteudo: (
        <p>
          Campos e itens <strong>desativados</strong> deixam de aparecer em checklists novos, mas tudo o que já foi
          respondido é preservado. Não há exclusão de estrutura — é assim que um checklist antigo continua legível
          mesmo depois de a oficina mudar a vistoria.
        </p>
      ),
    },
    {
      titulo: 'Permissões de Edição',
      conteudo: (
        <>
          <p>
            Por padrão, <strong>ninguém</strong> edita checklist concluído — cada permissão precisa ser concedida
            aqui, por perfil: <strong>Editar itens</strong> (status e observações de sessões finalizadas) e{' '}
            <strong>Adicionar/remover mídias</strong>. O administrador sempre tem acesso total e não aparece na
            lista.
          </p>
          <p>
            Nada aqui se perde: cada mudança nesta matriz fica registrada com quem fez, quando, e quais perfis
            entraram ou saíram — o botão <strong>Ver histórico desta matriz</strong>, no topo desta aba, abre a
            lista. Já as edições feitas <em>nos checklists</em> (itens, observações, fotos) aparecem na tela de{' '}
            <strong>Auditoria</strong>, na linha do tempo do veículo, porque são fatos daquele atendimento.
          </p>
        </>
      ),
    },
    {
      titulo: 'Mensagem de Compartilhamento',
      conteudo: (
        <p>
          É o texto que o cliente recebe no WhatsApp com o link do checklist. Os marcadores{' '}
          <strong>{'{{link}}'}</strong> (obrigatório), <strong>{'{{placa}}'}</strong> e{' '}
          <strong>{'{{nome}}'}</strong> são substituídos automaticamente. Sem o marcador do link a mensagem não é
          salva.
        </p>
      ),
    },
  ];

  return (
    <>
      <div className="admin-aba-cabecalho">
        <div className="admin-card-title">
          Checklist
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      {/* Sub-abas */}
      <div className="admin-tabs-container" style={{ marginBottom: 20 }}>
        {FIELD_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`admin-tab-btn ${subTab === tab.key ? 'active' : ''}`}
            onClick={() => setSubTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ padding: 20, color: 'var(--tema-texto-2, #5E5E5E)' }}>Carregando...</div>}

      {/* Campos simples (ângulos, categorias) */}
      {!loading && isFieldTab && (
        <>
          <div className="admin-card">
            <div className="admin-card-title">Criar novo campo — {currentTab.label}</div>
            <p style={{ color: 'var(--tema-texto-2, #5E5E5E)', fontSize: 13, marginBottom: 16 }}>
              Campos desativados deixam de aparecer em checklists novos, mas os registros históricos são preservados.
            </p>
            <div className="admin-form-grid">
              <div className="admin-input-group">
                <label className="admin-label">Nome</label>
                <input
                  type="text"
                  className="admin-input"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder={subTab === 'angles' ? 'Ex: Teto' : 'Ex: Itens do Motor'}
                />
              </div>
              {currentTab.hasHelp && (
                <div className="admin-input-group">
                  <label className="admin-label">Texto de ajuda (opcional)</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={newHelpText}
                    onChange={(e) => setNewHelpText(e.target.value)}
                    placeholder="Ex: Foto do teto do veículo"
                  />
                </div>
              )}
              <button
                className="admin-btn admin-btn-primary"
                style={{ height: 40 }}
                disabled={creating}
                onClick={handleCreate}
              >
                {creating ? 'Criando...' : 'Adicionar'}
              </button>
            </div>
          </div>

          <p className="admin-hint">
            A ordem define como {subTab === 'angles' ? 'as posições de foto aparecem' : 'as categorias aparecem'} no
            preenchimento do checklist, na visualização e no PDF — inclusive nos checklists já realizados. Use as setas
            para reordenar; campos desativados ficam no fim da lista.
          </p>

          {fields.map((field) => {
            const activeIndex = activeFields.findIndex((f) => f.id === field.id);
            return (
            <div key={field.id} className="admin-card" style={{ opacity: field.isActive ? 1 : 0.6, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {field.isActive
                    ? renderReorderButtons(activeIndex, activeFields.length, (direction) => handleMoveField(activeIndex, direction))
                    : null}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {renderInlineLabel(field.id, field.label, !field.isActive)}
                    <span style={{ fontSize: 12, color: 'var(--tema-texto-3, #6b7280)' }}>
                      Chave: <code style={{ background: 'var(--tema-fundo, #f1f3f4)', padding: '2px 4px', borderRadius: 4 }}>{field.key}</code>
                      {' '}| Ordem: <strong>{field.sortOrder}</strong>
                      {field.helpText ? <> | Ajuda: {field.helpText}</> : null}
                    </span>
                  </div>
                </div>
                <button
                  className={`admin-btn ${field.isActive ? 'admin-btn-danger-outline' : 'admin-btn-success-outline'}`}
                  style={{ padding: '6px 16px', fontSize: 12 }}
                  onClick={() => handleToggleField(field)}
                >
                  {field.isActive ? 'Desativar' : 'Reativar'}
                </button>
              </div>
            </div>
            );
          })}
        </>
      )}

      {/* Itens */}
      {!loading && subTab === 'items' && (
        <>
          <div className="admin-card">
            <div className="admin-card-title">Criar novo item de checklist</div>
            <div className="admin-form-grid">
              <div className="admin-input-group">
                <label className="admin-label">Nome do item</label>
                <input
                  type="text"
                  className="admin-input"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Ex: Chave reserva"
                />
              </div>
              <div className="admin-input-group fixed-width">
                <label className="admin-label">Categoria</label>
                <select
                  className="admin-select"
                  value={newCategoryId}
                  onChange={(e) => setNewCategoryId(e.target.value)}
                >
                  {categories.filter((c) => c.isActive).map((category) => (
                    <option key={category.id} value={category.id}>{category.label}</option>
                  ))}
                </select>
              </div>
              <button
                className="admin-btn admin-btn-primary"
                style={{ height: 40 }}
                disabled={creating}
                onClick={handleCreate}
              >
                {creating ? 'Criando...' : 'Adicionar'}
              </button>
            </div>
          </div>

          <p className="admin-hint">
            A ordem define como os itens aparecem dentro da categoria no preenchimento do checklist, na visualização e
            no PDF — inclusive nos checklists já realizados. Use as setas para reordenar; itens desativados ficam no fim
            da categoria.
          </p>

          {categories.map((category) => {
            const itemsOfCategory = items.filter((item) => item.categoryId === category.id);
            if (itemsOfCategory.length === 0) return null;
            const activeItemsOfCategory = itemsOfCategory.filter((item) => item.isActive);
            return (
              <div key={category.id} className="admin-group-section">
                <div className="admin-group-title">
                  {category.label}
                  {!category.isActive && <span className="admin-badge-inactive" style={{ marginLeft: 8 }}>CATEGORIA INATIVA</span>}
                </div>
                {itemsOfCategory.map((item) => {
                  const activeIndex = activeItemsOfCategory.findIndex((i) => i.key === item.key);
                  return (
                  <div key={item.key} className="admin-card" style={{ opacity: item.isActive ? 1 : 0.6, marginBottom: 10, padding: '14px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {item.isActive
                          ? renderReorderButtons(
                              activeIndex,
                              activeItemsOfCategory.length,
                              (direction) => handleMoveItem(category.id, activeIndex, direction),
                            )
                          : null}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {renderInlineLabel(item.key, item.label, !item.isActive)}
                          <span style={{ fontSize: 12, color: 'var(--tema-texto-3, #6b7280)' }}>
                            Chave: <code style={{ background: 'var(--tema-fundo, #f1f3f4)', padding: '2px 4px', borderRadius: 4 }}>{item.key}</code>
                            {' '}| Ordem: <strong>{item.sortOrder}</strong>
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <select
                          className="admin-select"
                          style={{ height: 34, fontSize: 12 }}
                          value={item.categoryId}
                          onChange={(e) => handleChangeItemCategory(item, e.target.value)}
                        >
                          {categories.filter((c) => c.isActive || c.id === item.categoryId).map((c) => (
                            <option key={c.id} value={c.id}>{c.label}</option>
                          ))}
                        </select>
                        <button
                          className={`admin-btn ${item.isActive ? 'admin-btn-danger-outline' : 'admin-btn-success-outline'}`}
                          style={{ padding: '6px 16px', fontSize: 12 }}
                          onClick={() => handleToggleItem(item)}
                        >
                          {item.isActive ? 'Desativar' : 'Reativar'}
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            );
          })}
        </>
      )}

      {/* Permissões de edição */}
      {!loading && subTab === 'permissions' && (
        <>
          <div className="admin-card" style={{ marginBottom: 16 }}>
            <div className="admin-card-title">Quem pode editar checklists concluídos</div>
            <p style={{ color: 'var(--tema-texto-2, #5E5E5E)', fontSize: 13 }}>
              O perfil <strong>ADMIN</strong> sempre tem acesso total e não aparece na lista.
              Toda edição de sessão e toda mudança nesta matriz são registradas em auditoria.
            </p>
            {/* at-69 (BUG-V61): a frase acima já prometia o registro desde a matriz dinâmica,
                e não havia onde vê-lo. A edição de SESSÃO aparece na Auditoria do veículo;
                a mudança da MATRIZ é o que este botão abre. */}
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              style={{ marginTop: 10 }}
              onClick={() => setIsHistoricoOpen(true)}
            >
              Ver histórico desta matriz
            </button>
          </div>

          {permissions.map((permission) => {
            const meta = PERMISSION_LABELS[permission.action] || { title: permission.action, description: '' };
            return (
              <div key={permission.action} className="admin-card" style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--tema-texto, #1A1C1B)' }}>{meta.title}</div>
                {meta.description && (
                  <p style={{ color: 'var(--tema-texto-2, #5E5E5E)', fontSize: 12, margin: '6px 0 12px' }}>{meta.description}</p>
                )}
                <div className="admin-role-pills">
                  {ALL_ROLES.map((role) => {
                    const isAllowed = permission.allowedRoles.includes(role);
                    return (
                      <button
                        key={role}
                        onClick={() => handleTogglePermissionRole(permission, role)}
                        className={`admin-role-pill ${isAllowed ? 'active' : ''}`}
                      >
                        {isAllowed ? '✓ ' : ''}
                        {role}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Template da mensagem de compartilhamento via WhatsApp */}
      {!loading && subTab === 'share-template' && (
        <div className="admin-card">
          <div className="admin-card-title">Mensagem enviada ao compartilhar o checklist via WhatsApp</div>
          <p style={{ color: 'var(--tema-texto-2, #5E5E5E)', fontSize: 13, marginBottom: 12 }}>
            Marcadores disponíveis: <code>{'{{link}}'}</code> (obrigatório — vira o link de acesso),{' '}
            <code>{'{{placa}}'}</code> e <code>{'{{nome}}'}</code> (nome do proprietário).
          </p>
          <textarea
            className="admin-input"
            style={{ width: '100%', minHeight: 140, resize: 'vertical', fontFamily: 'inherit', fontSize: 14, padding: 12 }}
            value={shareTemplate}
            onChange={(e) => setShareTemplate(e.target.value)}
          />
          <div style={{ marginTop: 12 }}>
            <button
              className="admin-btn admin-btn-primary"
              disabled={savingTemplate}
              onClick={async () => {
                if (!shareTemplate.includes('{{link}}')) {
                  toast.error('O template deve conter o marcador {{link}}.');
                  return;
                }
                setSavingTemplate(true);
                try {
                  await updateChecklistShareTemplate(shareTemplate);
                  toast.success('Template salvo com sucesso.');
                } catch (e: any) {
                  toast.error(e?.message || 'Erro ao salvar o template.');
                } finally {
                  setSavingTemplate(false);
                }
              }}
            >
              {savingTemplate ? 'Salvando...' : 'Salvar template'}
            </button>
          </div>
        </div>
      )}

      <HistoricoAlteracoes
        isOpen={isHistoricoOpen}
        onClose={() => setIsHistoricoOpen(false)}
        entity="checklist_edit_permission"
        titulo="Histórico da matriz de permissões"
        descricao={
          'Cada mudança nesta matriz fica registrada com quem fez, quando, e quais perfis entraram ou saíram. ' +
          'As edições feitas nos checklists em si aparecem na Auditoria do veículo, não aqui.'
        }
        rotuloAlvo="Permissão"
      />

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Checklist"
        secoes={helpSections}
      />
    </>
  );
};

export default ChecklistAdminSection;
